'use client';

import React, { useRef, useState } from 'react';

export interface SegmentedDecimalInputProps {
  /** Current value ('45.5', '45.50', '' …). Emitted with '.' as the decimal separator. */
  value: string;
  onChange: (value: string) => void;
  /** Number of whole-number digit blocks (default 3 → max 999) */
  wholeDigits?: number;
  /** Number of fraction digit blocks (default 2) */
  fractionDigits?: number;
  disabled?: boolean;
  /** Visible separator between the whole and fraction blocks (default ',') */
  separator?: string;
  /** Optional unit shown after the last block (e.g. 'L') */
  suffix?: string;
}

/**
 * MFA-style segmented decimal input: fixed digit blocks that auto-advance
 * on entry and move back on Backspace, laid out as `XXX,XX`. The stored
 * value always uses '.' as the decimal separator and is normalised to the
 * full fraction width on blur (e.g. '45.5' → '45.50').
 */
export function SegmentedDecimalInput({
  value,
  onChange,
  wholeDigits = 3,
  fractionDigits = 2,
  disabled = false,
  separator = ',',
  suffix,
}: SegmentedDecimalInputProps) {
  const totalDigits = wholeDigits + fractionDigits;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const valueToDigits = (v: string): string[] => {
    const raw = (v || '').replace(',', '.');
    const [wRaw = '', fRaw = ''] = raw.split('.');
    const w = wRaw.replace(/\D/g, '').slice(-wholeDigits);
    const f = fRaw.replace(/\D/g, '').slice(0, fractionDigits);
    return [
      ...Array<string>(wholeDigits - w.length).fill(''),
      ...w.split(''),
      ...f.split(''),
      ...Array<string>(fractionDigits - f.length).fill(''),
    ];
  };

  const digitsToValue = (d: string[]): string => {
    const w = d.slice(0, wholeDigits).join('');
    const f = d.slice(wholeDigits).join('');
    if (!w && !f) return '';
    return f ? `${w || '0'}.${f}` : w;
  };

  const [digits, setDigits] = useState<string[]>(() => valueToDigits(value));
  const [prevValue, setPrevValue] = useState(value || '');

  // Adopt external value changes (AI pre-fill, form reset) only when the
  // digit content actually differs — keeps typed block positions stable.
  if (value !== prevValue) {
    setPrevValue(value || '');
    const next = valueToDigits(value || '');
    if (next.join('') !== digits.join('')) {
      setDigits(next);
    }
  }

  const commit = (next: string[]) => {
    setDigits(next);
    onChange(digitsToValue(next));
  };

  const focusBox = (index: number) => {
    setTimeout(() => inputRefs.current[index]?.focus(), 0);
  };

  const handleChange = (raw: string, index: number) => {
    if (disabled) return;

    // Typing the decimal separator in a whole block jumps to the fraction blocks
    if (raw === '.' || raw === ',') {
      if (index < wholeDigits) focusBox(wholeDigits);
      return;
    }

    const chars = raw.replace(/\D/g, '');
    const next = [...digits];
    if (!chars) {
      next[index] = '';
      commit(next);
      return;
    }
    chars.slice(0, totalDigits - index).split('').forEach((ch, i) => {
      next[index + i] = ch;
    });
    commit(next);
    focusBox(Math.min(index + chars.length, totalDigits - 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next);
      focusBox(index - 1);
      return;
    }
    if ((e.key === '.' || e.key === ',') && index < wholeDigits) {
      e.preventDefault();
      focusBox(wholeDigits);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusBox(index - 1);
      return;
    }
    if (e.key === 'ArrowRight' && index < totalDigits - 1) {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (disabled) return;
    const text = e.clipboardData.getData('text');
    if (!text) return;

    let next: string[];
    if (text.includes('.') || text.includes(',')) {
      // '45.5' / '45,50' → split at the separator
      const [wRaw = '', fRaw = ''] = text.replace(',', '.').split('.');
      const w = wRaw.replace(/\D/g, '').slice(-wholeDigits);
      const f = fRaw.replace(/\D/g, '').slice(0, fractionDigits);
      next = [
        ...Array<string>(wholeDigits - w.length).fill(''),
        ...w.split(''),
        ...f.split(''),
        ...Array<string>(fractionDigits - f.length).fill(''),
      ];
    } else {
      // Bare digits: more digits than whole blocks → last digits are the fraction
      const all = text.replace(/\D/g, '').slice(0, totalDigits);
      const splitAt = Math.max(0, all.length - fractionDigits);
      const w = all.slice(0, Math.min(splitAt, wholeDigits));
      const f = all.length > wholeDigits ? all.slice(splitAt) : '';
      next = [
        ...Array<string>(wholeDigits - w.length).fill(''),
        ...w.split(''),
        ...f.split(''),
        ...Array<string>(fractionDigits - f.length).fill(''),
      ];
    }
    commit(next);
    const firstEmpty = next.findIndex((d) => d === '');
    focusBox(firstEmpty === -1 ? totalDigits - 1 : firstEmpty);
  };

  // Normalise to the full XXX.XX shape when focus leaves the control.
  const handleBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node | null)) return;
    const v = digitsToValue(digits);
    if (!v) return;
    const [w = '', f = ''] = v.split('.');
    const wNorm = w.replace(/^0+(?=\d)/, '') || '0';
    const normalized = `${wNorm}.${f.padEnd(fractionDigits, '0')}`;
    if (normalized !== v) onChange(normalized);
  };

  const renderInput = (digit: string, index: number) => (
    <input
      key={index}
      ref={(el) => { inputRefs.current[index] = el; }}
      type="text"
      inputMode="decimal"
      maxLength={1}
      value={digit}
      disabled={disabled}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      placeholder="0"
      aria-label={`Digit ${index + 1}`}
      onChange={(e) => handleChange(e.target.value, index)}
      onKeyDown={(e) => handleKeyDown(e, index)}
      onPaste={handlePaste}
      onFocus={(e) => e.target.select()}
      onBlur={handleBlur}
      className="h-12 w-10 rounded-xl border border-gray-200 bg-white text-center font-mono text-lg font-semibold text-gray-900 placeholder-gray-300 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500 dark:focus:ring-blue-500/30 sm:w-11"
    />
  );

  return (
    <div ref={containerRef} className="flex items-center gap-1.5">
      {digits.slice(0, wholeDigits).map((digit, i) => renderInput(digit, i))}
      <span className="-mt-1 select-none px-0.5 text-2xl font-bold leading-none text-gray-400 dark:text-gray-500">
        {separator}
      </span>
      {digits.slice(wholeDigits).map((digit, i) => renderInput(digit, wholeDigits + i))}
      {suffix && (
        <span className="ml-1 text-sm font-semibold text-gray-400 dark:text-gray-500">{suffix}</span>
      )}
    </div>
  );
}
